import React, { memo } from 'react';
import Select, { components } from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';
import { ChevronDown, Cross } from '@saleshandy/icons';
import { SelectFilterProps } from './type';

const Option = (props) => {
  const { isMultiSelect, isSelected, isDisabled, children } = props;
  return (
    <components.Option {...props} className="d-flex align-items-center">
      <div
        className="d-flex align-items-center"
        style={{
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          pointerEvents: isDisabled ? 'none' : 'auto',
        }}
      >
        {isMultiSelect && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => null}
            disabled={isDisabled}
            style={{
              accentColor: isSelected ? '#FB6414' : '#FFFFFF',
              border: '1px solid #9CA3AF',
              borderRadius: '4px',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              width: '16px',
              height: '16px',
              opacity: isDisabled ? 0.5 : 1,
            }}
          />
        )}{' '}
        <span
          className="ml-2 regular-2 popover-arrow-color-txt"
          style={{
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isDisabled ? 0.6 : 1,
          }}
        >
          {children}
        </span>
      </div>
    </components.Option>
  );
};

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <ChevronDown width={16} height={16} color="#6B7280" />
  </components.DropdownIndicator>
);

const clearIndicator = (props) => (
  <components.ClearIndicator {...props}>
    <Cross width={16} height={16} color="#6B7280" />
  </components.ClearIndicator>
);

// Custom MultiValue that shows first value and "+X" for additional values
const CustomMultiValue = (props: any) => {
  const { data, selectProps, showCount: propShowCount } = props;
  const selectedValues = selectProps.value || [];
  const additionalCount = selectedValues.length - 1;
  // Use showCount prop if provided, otherwise check className for backward compatibility
  const showCount =
    propShowCount ?? selectProps.className?.includes('value-select');

  if (!showCount) {
    // Use default MultiValue for non-value-select cases
    return <components.MultiValue {...props} />;
  }

  // Get the unique key function
  const getOptionValue =
    selectProps.getOptionValue || ((opt: any) => opt.value);
  const firstValueKey =
    selectedValues.length > 0 ? getOptionValue(selectedValues[0]) : null;
  const currentValueKey = getOptionValue(data);
  const isFirstValue = firstValueKey === currentValueKey;

  if (!isFirstValue) {
    // Hide additional values beyond the first one
    return null;
  }

  return (
    <components.MultiValue {...props}>
      <div className="d-flex align-items-center" style={{ gap: '4px' }}>
        <span>{selectProps.getOptionLabel(data)}</span>
        {additionalCount > 0 && (
          <span
            className="additional-count-badge"
            style={{
              marginLeft: '4px',
              padding: '2px 6px',
              backgroundColor: '#E4E7EC',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 500,
              color: '#1F2937',
            }}
          >
            +{additionalCount}
          </span>
        )}
      </div>
    </components.MultiValue>
  );
};

/**
 * Styles to pass in `react-select`
 */
const textStyle = {
  fontSize: '14px',
  fontStyle: 'normal',
  lineHeight: '20px',
  color: '#6b7280',
};

const SelectFilter: React.FC<SelectFilterProps> = ({
  placeholder,
  options,
  labelKey = 'name',
  onSelect,
  uniqueKey = 'id',
  values = [],
  isMultiSelect,
  className,
  disabled = false,
  showCount = false,
}) => {
  const getOptionLabel = (option) => option[labelKey];
  const getOptionValue = (option) => option[uniqueKey];

  return (
    <Select
      value={isMultiSelect ? values : values[0] || null}
      isMulti={isMultiSelect}
      closeMenuOnSelect={!isMultiSelect}
      hideSelectedOptions={false}
      name="colors"
      options={options}
      className={`basic-multi-select ${className}`}
      classNamePrefix="select"
      isDisabled={disabled}
      // components={animatedComponents}
      components={{
        Option: (props) => <Option isMultiSelect={isMultiSelect} {...props} />,
        DropdownIndicator,
        ClearIndicator: clearIndicator,
        ...(isMultiSelect
          ? {
              MultiValue: (props: any) => (
                <CustomMultiValue {...props} showCount={showCount} />
              ),
            }
          : { MultiValueRemove: () => null }),
      }}
      isOptionSelected={(option) =>
        values.some((op) => op[uniqueKey] === option[uniqueKey])
      }
      onChange={(selectedOption) => {
        if (isMultiSelect) {
          onSelect(selectedOption);
        } else {
          onSelect([selectedOption]);
        }
      }}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      placeholder={placeholder}
      styles={{
        control: (base) => ({
          ...base,
          border: '1px solid #E4E7EC',
          ':hover': {
            border: '1px solid #E4E7EC',
          },
        }),
        placeholder: (base) => ({
          ...base,
          ...textStyle,
          color: '#98a2b3',
        }),
        option: (base, { isSelected, isDisabled }) => {
          let backgroundColor = '#fff';
          if (isSelected) {
            backgroundColor = '#FFF6ED';
          } else if (isDisabled) {
            backgroundColor = '#F9FAFB';
          }

          return {
            ...base,
            ...textStyle,
            backgroundColor,
            color: isDisabled ? '#9CA3AF' : textStyle.color,
            wordWrap: 'break-word',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            ':active': {
              backgroundColor: isSelected ? '#FFF6ED' : '#fff',
            },
            ':hover': {
              backgroundColor: isDisabled ? '#F9FAFB' : '#FFF6ED',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
            },
          };
        },
        multiValue: (base) => ({
          ...base,
          marginRight: '8px',
          borderRadius: '4px',
          border: isMultiSelect ? '1px solid #E4E7EC' : 'none',
        }),
        multiValueLabel: (base) => ({
          ...base,
          ...textStyle,
          fontWeight: 500,
          color: '#1F2937',
          backgroundColor: '#fff',
          borderRadius: '0px',
        }),
        multiValueRemove: (base) => ({
          ...base,
          marginRight: '0px',
          color: '#4b5563',
          backgroundColor: '#fff',
          '&:hover': {
            backgroundColor: '#fff',
            color: '#4b5563',
          },
        }),
        indicatorsContainer: (base) => ({
          ...base,
          color: '#9ca3af',
        }),
        menuList: (base) => ({
          ...base,
          color: '#fff',
        }),
        input: (base) => ({
          ...base,
          color: !isMultiSelect ? '#fff' : '#333333',
        }),
      }}
    />
  );
};

export default memo(SelectFilter);
